import { gql, type TypedDocumentNode } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconPen } from '@sorare/core/src/atoms/icons/system/iconPen.ts';
import { iconXCircle } from '@sorare/core/src/atoms/icons/system/iconXCircle.ts';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LinkOther } from '@sorare/core/src/atoms/navigation/Box';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { useDeleteTaskLineup } from '../useDeleteTaskLineup';
import type { LineupButtons_lineupInterface } from './__generated__/index.graphql.ts';

type Props = {
  lineup?: LineupButtons_lineupInterface;
  composeTeamUrl: string;
  onDeleteDone?: () => void;
};
export const LineupButtons = ({
  lineup,
  composeTeamUrl,
  onDeleteDone,
}: Props) => {
  const [deleteTaskLineup] = useDeleteTaskLineup();
  if (!lineup) {
    return (
      <LinkOther
        as={Button}
        color="transparent"
        to={composeTeamUrl}
        fullWidth
        size="medium"
      >
        <Horizontal>
          <FontAwesomeIcon icon={iconPen} />
          <BodyS as="p" bold>
            <FormattedMessage {...fantasy.addTeam} />
          </BodyS>
        </Horizontal>
      </LinkOther>
    );
  }
  const { deletable, updatable } = lineup;
  if (deletable || updatable) {
    return (
      <Horizontal>
        {!!deletable && (
          <LinkOther
            as={Button}
            size="medium"
            color="transparent"
            fullWidth
            onClick={async () => {
              await deleteTaskLineup({
                variables: {
                  input: {
                    taskLineupId: lineup.id,
                  },
                },
              });
              onDeleteDone?.();
            }}
          >
            <Horizontal>
              <FontAwesomeIcon icon={iconXCircle} />
              <BodyS as="p" bold>
                <FormattedMessage defaultMessage="Clear Team" />
              </BodyS>
            </Horizontal>
          </LinkOther>
        )}
        {!!updatable && (
          <LinkOther
            as={Button}
            color="transparent"
            to={composeTeamUrl}
            fullWidth
            size="medium"
          >
            <Horizontal>
              <FontAwesomeIcon icon={iconPen} />
              <BodyS as="p" bold>
                <FormattedMessage {...fantasy.editLineup} />
              </BodyS>
            </Horizontal>
          </LinkOther>
        )}
      </Horizontal>
    );
  }
  return null;
};

LineupButtons.fragments = {
  lineupInterface: gql`
    fragment LineupButtons_lineupInterface on LineupInterface {
      id
      deletable
      updatable
    }
  ` as TypedDocumentNode<LineupButtons_lineupInterface>,
};
